import { Query } from "@apollo/client/react/components";
import { Button } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import { ProjectCard } from "@govlaunch/project";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { FormattedMessage } from "react-intl";
import useIsMobile from "~/lib/hooks/useIsMobile";
import VerifiedGovernmentProjectsQuery from "~/lib/queries/VerifiedGovernmentProjectsQuery";
import {
  IVerifiedGovernmentProjectsQuery,
  IVerifiedGovernmentProjectsQueryVariables,
} from "~/lib/queries/__generated__/VerifiedGovernmentProjectsQuery.generated";
import { useIntlLocale } from "~/lib/withIntl";
import TranslationButton from "~/src/components/translation/TranslationButton";
import { appendNextLinkHrefParams } from "~/src/utils/nextLinkHref";
import mediaQueries from "~/components/mediaQueries";

interface IVerifiedGovernmentProjectsProps {
  governmentSlug: string;
}

export default function VerifiedGovernmentProjects({ governmentSlug }: IVerifiedGovernmentProjectsProps) {
  const router = useRouter();
  const isMobile = useIsMobile();
  const { locale } = useIntlLocale();

  return (
    <Query<IVerifiedGovernmentProjectsQuery, IVerifiedGovernmentProjectsQueryVariables>
      query={VerifiedGovernmentProjectsQuery}
      variables={{
        slug: governmentSlug,
        cursor: null,
      }}
    >
      {({ data, fetchMore }) => {
        if (!data || !data.government) {
          return null;
        }

        const { verifiedProjects } = data.government;

        if (!verifiedProjects.items || verifiedProjects.items.length === 0) {
          return null;
        }

        return (
          <div
            css={{
              display: "grid",
              gridRowGap: 24,
            }}
          >
            <div>
              <h2
                css={{
                  margin: "0 0 16px 0",
                  color: palette.sealBlue,
                  fontWeight: 400,
                  fontSize: 16,
                }}
              >
                <FormattedMessage defaultMessage="Verified Projects" id="uxuBI+" />
              </h2>

              <div
                css={mediaQueries({
                  display: "grid",
                  gridRowGap: 16,
                  gridColumnGap: 16,
                  gridTemplateColumns: ["1fr", "1fr 1fr"],
                })}
              >
                {verifiedProjects.items.map((project) => {
                  const linkHref = isMobile
                    ? `/projects/${project.slug}`
                    : appendNextLinkHrefParams(router, {
                        projectSlug: project.slug,
                      });

                  const translationLinkHref = isMobile
                    ? `/projects/${project.slug}/${locale}`
                    : appendNextLinkHrefParams(router, {
                        projectSlug: project.slug,
                        lang: locale,
                      });

                  const translationLinkWrap = () => {
                    return (
                      <TranslationButton
                        paddingY="4px"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          router.push(translationLinkHref);
                        }}
                      />
                    );
                  };

                  return (
                    <Link
                      href={linkHref}
                      as={
                        isMobile
                          ? `/projects/${project.slug}`
                          : `/governments/${governmentSlug}/projects/${project.slug}`
                      }
                      passHref={true}
                      key={project._id}
                      scroll={false}
                    >
                      <ProjectCard
                        project={project}
                        firstLocationId={data?.government?._id}
                        translationLinkWrap={locale !== "en-US" ? translationLinkWrap : undefined}
                      />
                    </Link>
                  );
                })}
              </div>
            </div>

            {verifiedProjects.pageInfo && verifiedProjects.pageInfo.hasNextPage && verifiedProjects.pageInfo.endCursor && (
              <Button
                onClick={() => {
                  if (verifiedProjects.pageInfo && verifiedProjects.pageInfo.endCursor) {
                    fetchMore({
                      variables: {
                        slug: governmentSlug,
                        cursor: verifiedProjects.pageInfo.endCursor,
                      },
                      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                        if (
                          !previousQueryResult.government ||
                          !previousQueryResult.government.verifiedProjects.items ||
                          !fetchMoreResult ||
                          !fetchMoreResult.government ||
                          !fetchMoreResult.government.verifiedProjects ||
                          !fetchMoreResult.government.verifiedProjects.items
                        ) {
                          return previousQueryResult;
                        }

                        return {
                          ...previousQueryResult,
                          groupBySlug: {
                            ...previousQueryResult.government,
                            verifiedProjects: {
                              ...fetchMoreResult.government.verifiedProjects,
                              items: [
                                ...previousQueryResult.government.verifiedProjects.items,
                                ...fetchMoreResult.government.verifiedProjects.items,
                              ],
                            },
                          },
                        };
                      },
                    });
                  }
                }}
                css={{
                  zIndex: 9999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  margin: 1,
                  padding: 10,
                  color: palette.primary,
                  fontWeight: 500,
                  fontSize: 14,
                  lineHeight: 1.33,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  backgroundColor: "transparent",
                  border: "none",
                  outline: "none",
                  cursor: "pointer",
                  "&:active": {
                    opacity: 0.7,
                  },
                  marginmt: 0,
                }}
                disabled={false}
              >
                <FormattedMessage defaultMessage="Show more" id="aWpBzj" />
              </Button>
            )}
          </div>
        );
      }}
    </Query>
  );
}
